import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faCheck,
  faClock,
  faRotate,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import api from "../../services/api";

const LABELS = {
  APPROVED: "Aprovado",
  PENDING: "Pendente",
  AWAITING_APPROVAL: "Em análise",
  REJECTED: "Rejeitado",
  NOT_SENT: "Não enviado",
};

function RegistrationItem({ label, value }) {
  const status = String(value || "PENDING").toUpperCase();
  const approved = status === "APPROVED";
  const rejected = status === "REJECTED";

  return (
    <div className="fxpay-step">
      <span>
        <FontAwesomeIcon
          icon={approved ? faCheck : rejected ? faTriangleExclamation : faClock}
        />
      </span>
      <div>
        <b>{label}</b>
        <small>{LABELS[status] || status}</small>
      </div>
    </div>
  );
}

export default function FluxopayOnboardingPanel({ onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/api/fluxopay/onboarding/status");
      setData(response.data);
      if (response.data?.account_status === "active") onUpdated?.();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          "Não foi possível consultar a análise cadastral.",
      );
    } finally {
      setLoading(false);
    }
  }, [onUpdated]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return <section className="fxpay-card">Consultando análise cadastral...</section>;
  }

  const registration = data?.registration || {};
  const documents = data?.documents || [];

  return (
    <section className="fxpay-card">
      <header>
        <div>
          <small>ANÁLISE CADASTRAL</small>
          <h3>Ativação da conta no Asaas</h3>
        </div>
        <button
          type="button"
          className="fxpay-icon-button"
          title="Atualizar situação"
          onClick={load}
          disabled={loading}
        >
          <FontAwesomeIcon icon={faRotate} />
        </button>
      </header>

      {error && <p className="fxpay-warning">{error}</p>}

      <div className="fxpay-account-steps">
        <RegistrationItem label="Aprovação geral do cadastro" value={registration.general} />
        <RegistrationItem label="Dados comerciais" value={registration.commercial_info} />
        <RegistrationItem label="Dados bancários" value={registration.bank_account_info} />
        <RegistrationItem label="Documentos" value={registration.documentation} />
      </div>

      {documents.length > 0 && (
        <div className="fxpay-onboarding-documents">
          <h4>Pendências documentais</h4>
          {documents.map((document, index) => (
            <div key={document.id || `${document.type}-${index}`} className="fxpay-step">
              <span><FontAwesomeIcon icon={document.status === "APPROVED" ? faCheck : faClock} /></span>
              <div>
                <b>{document.title}</b>
                <small>{LABELS[document.status] || document.status}</small>
                {document.description && <small>{document.description}</small>}
                {document.onboarding_url &&
                  ["NOT_SENT", "PENDING", "REJECTED"].includes(
                    document.status
                  ) && (
                  <a
                    className="fxpay-primary"
                    href={document.onboarding_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Continuar no ambiente seguro do Asaas
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {data?.next_action === "await_analysis" && (
        <p className="fxpay-note">
          Os dados já foram enviados e estão aguardando análise do Asaas.
        </p>
      )}

      {registration.bank_account_info === "PENDING" && (
        <p className="fxpay-note">
          Os dados bancários ainda precisam ser concluídos no fluxo seguro indicado pelo Asaas.
        </p>
      )}

      <p className="fxpay-note">
        Documentos e validação facial são enviados diretamente ao Asaas e não ficam armazenados no Fluxo Clínica.
      </p>
    </section>
  );
}
